import { useEffect, useState } from 'react';

/**
 * SSR-safe `matchMedia` hook. Returns whether `query` currently matches.
 *
 * On the server (or before mount), returns `defaultValue` so React's hydrated
 * markup matches the server output — preventing the hydration warning that
 * would otherwise occur if we read `window.matchMedia` from the initial state.
 * The real value is read in an effect after mount.
 *
 * Falls back to the deprecated `addListener` / `removeListener` API for older
 * Safari (< 14) which doesn't implement `addEventListener` on `MediaQueryList`.
 */
export function useMediaQuery(query: string, defaultValue = false): boolean {
  const [matches, setMatches] = useState<boolean>(defaultValue);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mq = window.matchMedia(query);
    setMatches(mq.matches);

    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    if (mq.addEventListener) {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    // Safari < 14 fallback
    mq.addListener(handler);
    return () => mq.removeListener(handler);
  }, [query]);

  return matches;
}
